import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import {
  Plus,
  Search,
  Tag,
  Trash2,
  Edit2,
  Clock,
  X,
  ChevronLeft,
  ChevronRight,
  Percent,
  Camera,
  Layers,
  IndianRupee,
} from "lucide-react";
import "./OfferScreen.css";
import NotFound from "../../components/NotFound/NotFound";
import { BASE_URL } from "../../constants/urls";

const CATEGORY_OPTIONS = [
  { value: "Hair", label: "Hair Cut", id: 1 },
  { value: "Skin", label: "Skin Care", id: 2 },
  { value: "Nails", label: "Nail Art", id: 3 },
  { value: "Makeup", label: "Makeup", id: 4 },
];

function OfferScreen() {
  const [offers, setOffers] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [imageError, setImageError] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 8,
  });

  const [categoryId, setCategoryId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [regularPrice, setRegularPrice] = useState("");
  const [offerPrice, setOfferPrice] = useState("");
  const [image, setImage] = useState(null);
  const [description, setDescription] = useState("");

  const token = localStorage.getItem("token");
  const searchTimeout = useRef(null);

  useEffect(() => {
    fetchOffers(1);
    fetchAllServices();
  }, []);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchOffers(1), 500);
    return () => clearTimeout(searchTimeout.current);
  }, [searchTerm]);

  const fetchAllServices = async () => {
    try {
      const res = await fetch(`${BASE_URL}/services?limit=100`, {
        headers: { Authorization: `${token}` },
      });
      const data = await res.json();
      if (data.services) setAllServices(data.services);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchOffers = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        search: searchTerm,
      });
      const res = await fetch(`${BASE_URL}/offers?${params}`, {
        headers: { Authorization: `${token}` },
      });
      const data = await res.json();
      if (data.offers) {
        setOffers(data.offers);
        if (data.pagination) setPagination(data.pagination);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelection = (sId) => {
    setServiceId(sId);
    const selected = allServices.find((s) => s.id === Number(sId));
    if (selected) {
      setRegularPrice(selected.rate);
      setCategoryId(selected.categoryId);
      setImage(selected?.images[0]);
      setImageError("");
    }
  };

  const handleSaveOffer = async (e) => {
    e.preventDefault();

    if (!image && !editingOffer) {
      setImageError("Service image is required to create an offer");
      return;
    }

    const submitData = {
      categoryId: Number(categoryId),
      serviceId: Number(serviceId),
      regularPrice: Number(regularPrice),
      offerPrice: Number(offerPrice),
      image,
      description,
    };

    setSubmitting(true);
    try {
      const url = editingOffer
        ? `${BASE_URL}/offers/${editingOffer.id}`
        : `${BASE_URL}/offers`;

      const res = await fetch(url, {
        method: editingOffer ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify(submitData),
      });

      const data = await res.json();

      if (res.ok) {
        Swal.fire(
          "Success",
          `Offer ${editingOffer ? "updated" : "created"}!`,
          "success"
        );
        fetchOffers(pagination.currentPage);
        closeModal();
      } else {
        if (data.message && data.message.includes("Image upload failed")) {
          setImageError("Image upload failed. Please try again.");
        } else {
          Swal.fire(
            "Action Denied",
            data.message || "Something went wrong",
            "error"
          );
        }
      }
    } catch (error) {
      Swal.fire("Error", "Connection to server failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Remove this special offer?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1a1a1a",
      confirmButtonText: "Yes, delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await fetch(`${BASE_URL}/offers/${id}`, {
            method: "DELETE",
            headers: { Authorization: `${token}` },
          });
          fetchOffers(pagination.currentPage);
          Swal.fire("Deleted!", "Offer removed.", "success");
        } catch (error) {
          console.error(error);
        }
      }
    });
  };

  const openModal = (offer = null) => {
    if (offer) {
      const matchedService =
        allServices.find((service) => service.id === offer.service?.id) || null;

      setEditingOffer(offer);
      setCategoryId(matchedService?.categoryId || "");
      setServiceId(offer.service?.id || "");
      setRegularPrice(offer.regularPrice);
      setOfferPrice(offer.offerPrice);
      setImage(offer.service?.images[0] || matchedService?.images[0] || null);
      setImageError("");
      setDescription(offer?.description ?? "");
    } else {
      setEditingOffer(null);
      setCategoryId("");
      setServiceId("");
      setRegularPrice("");
      setOfferPrice("");
      setImage(null);
      setImageError("");
      setDescription("");
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingOffer(null);
    setImageError("");
  };

  const calculateDiscount = (reg, off) => {
    if (!reg || !off) return 0;
    return Math.round(((reg - off) / reg) * 100);
  };

  const SkeletonCard = () => (
    <div className="of-card skeleton-card">
      <div className="of-img-wrap skeleton-media">
        <div className="skeleton-shimmer"></div>
      </div>
      <div className="of-body">
        <div className="skeleton-text skeleton-category"></div>
        <div className="skeleton-text skeleton-title"></div>
        <div className="of-pricing-row">
          <div className="skeleton-text skeleton-old-price"></div>
          <div className="skeleton-text skeleton-new-price"></div>
        </div>
      </div>
      <div className="of-foot">
        <div className="skeleton-text skeleton-meta"></div>
        <div className="of-actions">
          <div className="skeleton-icon"></div>
          <div className="skeleton-icon"></div>
        </div>
      </div>
    </div>
  );

  const ImageCarousel = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    let intervalRef = useRef(null);

    const hasMultipleImages = images && images.length > 1;

    const nextSlide = () => {
      setCurrentIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    };

    const prevSlide = () => {
      setCurrentIndex((prevIndex) =>
        prevIndex === 0 ? images.length - 1 : prevIndex - 1
      );
    };

    const goToSlide = (index) => {
      setCurrentIndex(index);
    };

    useEffect(() => {
      if (hasMultipleImages && isHovered) {
        intervalRef.current = setInterval(nextSlide, 3000);
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      }
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, [isHovered, images, hasMultipleImages]);

    if (!images || images.length === 0) {
      return (
        <div className="of-placeholder">
          <Tag size={40} />
        </div>
      );
    }

    return (
      <div
        className="of-carousel-container"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="of-carousel-slides">
          {images.map((img, index) => (
            <div
              key={index}
              className={`of-carousel-slide ${
                index === currentIndex ? "active" : ""
              }`}
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              <img src={img} alt={`Service ${index + 1}`} />
            </div>
          ))}
        </div>

        {hasMultipleImages && (
          <>
            <button
              className="of-carousel-btn of-carousel-prev"
              onClick={(e) => {
                e.stopPropagation();
                prevSlide();
              }}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className="of-carousel-btn of-carousel-next"
              onClick={(e) => {
                e.stopPropagation();
                nextSlide();
              }}
            >
              <ChevronRight size={20} />
            </button>

            <div className="of-carousel-dots">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`of-carousel-dot ${
                    index === currentIndex ? "active" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSlide(index);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="of-page">
      <header className="of-header">
        <div className="of-nav-brand">
          <div>
            <h1>Special Offers</h1>
            <p>Promote your services with custom deals</p>
          </div>
        </div>
        <div className="of-header-actions">
          <div className="of-search">
            <Search size={18} />
            <input
              placeholder="Search services in offers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="of-add-btn" onClick={() => openModal()}>
            <Plus size={20} /> <span>New Offer</span>
          </button>
        </div>
      </header>

      <main className="of-content">
        {loading ? (
          <div className="of-grid">
            {[...Array(8)].map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        ) : offers.length === 0 ? (
          <NotFound title={"Offers"} />
        ) : (
          <>
            <div className="of-grid">
              {offers.map((offer) => (
                <div key={offer.id} className="of-card">
                  <div className="of-img-wrap">
                    <ImageCarousel images={offer?.service?.images || []} />
                    <div className="of-discount-badge">
                      <Percent size={12} />{" "}
                      {calculateDiscount(offer.regularPrice, offer.offerPrice)}%
                      OFF
                    </div>
                  </div>
                  <div className="of-body">
                    <span className="of-category-tag">
                      {allServices.find(
                        (service) => service.id === offer.service?.id
                      )?.categoryName || "General"}
                    </span>
                    <h3>{offer.serviceName || offer.service?.name}</h3>
                    <div className="of-pricing-row">
                      <span className="of-old-price">
                        ₹{offer.regularPrice}
                      </span>
                      <span className="of-new-price">₹{offer.offerPrice}</span>
                    </div>
                    {console.log("OFFER-------------", offer)}
                    <div className="pricing-row">
                      <span className="">{offer.description}</span>
                    </div>
                  </div>
                  <div className="of-foot">
                    <div className="of-meta">
                      <Clock size={14} /> <span>Active</span>
                    </div>
                    <div className="of-actions">
                      <button onClick={() => openModal(offer)}>
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(offer.id)}
                        className="of-del"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="sv-pagination-wrapper">
              <div className="sv-pagination-modern">
                <button
                  className="sv-pag-nav"
                  disabled={pagination?.currentPage === 1}
                  onClick={() => fetchOffers(pagination.currentPage - 1)}
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="sv-pag-numbers">
                  <span className="sv-pag-current">
                    {pagination?.currentPage}
                  </span>
                  <span className="sv-pag-divider">/</span>
                  <span className="sv-pag-total">{pagination?.totalPages}</span>
                </div>
                <button
                  className="sv-pag-nav"
                  disabled={pagination?.currentPage === pagination?.totalPages}
                  onClick={() => fetchOffers(pagination?.currentPage + 1)}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      {modalOpen && (
        <div className="of-modal-backdrop">
          <div className="of-modal">
            <div className="of-modal-header">
              <div className="of-modal-title">
                <div className="of-icon-box">
                  {editingOffer ? <Edit2 size={20} /> : <Plus size={20} />}
                </div>
                <div>
                  <h2>{editingOffer ? "Edit Offer" : "New Offer"}</h2>
                  <p>Select service and set promotional price</p>
                </div>
              </div>
              <button className="of-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveOffer} className="of-form">
              <div className="of-form-layout">
                <div className="of-form-side">
                  <div className="of-preview-box">
                    {image ? (
                      <img src={image} alt="Service" />
                    ) : (
                      <div className="of-img-empty">
                        <Camera size={32} />
                      </div>
                    )}
                  </div>
                  {imageError && (
                    <div className="of-error-message">{imageError}</div>
                  )}
                  {!image && !editingOffer && !imageError && (
                    <div className="of-hint-text">Image is required *</div>
                  )}
                </div>

                <div className="of-form-main">
                  <div className="of-input-group">
                    <label>
                      <Layers size={14} /> Select Service
                    </label>
                    <select
                      value={serviceId}
                      onChange={(e) => handleServiceSelection(e.target.value)}
                      required
                    >
                      <option value="">Choose a service...</option>
                      {allServices.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} (₹{s.rate})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="of-row">
                    <div className="of-input-group">
                      <label>
                        <IndianRupee size={14} /> Regular Price
                      </label>
                      <input
                        type="number"
                        value={regularPrice}
                        readOnly
                        placeholder="0.00"
                      />
                    </div>
                    <div className="of-input-group">
                      <label>
                        <Percent size={14} /> Offer Price
                      </label>
                      <input
                        type="number"
                        value={offerPrice}
                        onChange={(e) => setOfferPrice(e.target.value)}
                        required
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="of-input-group">
                    <label>
                      <Percent size={14} /> Offer Description
                    </label>
                    <input
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                      placeholder="Add a small description on offer"
                    />
                  </div>

                  {regularPrice && offerPrice && (
                    <div className="of-savings-info">
                      Customer saves ₹{regularPrice - offerPrice} (
                      {calculateDiscount(regularPrice, offerPrice)}%)
                    </div>
                  )}
                </div>
              </div>

              <div className="of-modal-footer">
                <button
                  type="button"
                  className="of-cancel"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="of-submit"
                  disabled={submitting}
                >
                  {submitting
                    ? "Processing..."
                    : editingOffer
                    ? "Update Offer"
                    : "Create Offer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default OfferScreen;
